import { io, type Socket } from "socket.io-client";
import type { GameState } from "../engine/types";
import type { MultiplayerCredentials, MultiplayerGameUpdate, MultiplayerLobbyConfig, MultiplayerRoom } from "./types";

type Ack<T> = { ok: true } & T | { ok: false; code?: string; error: string };
const LAST_SESSION = "dd-multiplayer-last-session";
const sessionKey = (code: string) => `dd-multiplayer-session:${code.toUpperCase()}`;

export class MultiplayerError extends Error {
  constructor(message: string, public code = "MULTIPLAYER") { super(message); }
}

const configuredServer = () => (import.meta.env.VITE_MULTIPLAYER_SERVER as string | undefined) || location.origin;
const configuredPath = () => (import.meta.env.VITE_MULTIPLAYER_PATH as string | undefined) || "/socket.io";

export class MultiplayerClient {
  readonly socket: Socket;
  credentials: MultiplayerCredentials | null = null;
  room: MultiplayerRoom | null = null;
  revision = 0;

  constructor() {
    this.socket = io(configuredServer(), { path: configuredPath(), autoConnect: true, reconnection: true, transports: ["websocket", "polling"] });
  }

  get connected() { return this.socket.connected; }
  async waitForConnection() {
    if (this.socket.connected) return;
    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => { cleanup(); reject(new MultiplayerError("Multiplayer server is not responding.", "TIMEOUT")); }, 8000);
      const cleanup = () => { window.clearTimeout(timer); this.socket.off("connect", ready); this.socket.off("connect_error", failed); };
      const ready = () => { cleanup(); resolve(); }, failed = () => { cleanup(); reject(new MultiplayerError("Cannot connect to the multiplayer server.", "CONNECT")); };
      this.socket.once("connect", ready); this.socket.once("connect_error", failed);
      if (!this.socket.active) this.socket.connect();
    });
  }

  private async request<T>(event: string, payload: unknown): Promise<T> {
    await this.waitForConnection();
    return await new Promise<T>((resolve, reject) => {
      this.socket.timeout(10_000).emit(event, payload, (timeout: Error | null, response: Ack<T>) => {
        if (timeout) { reject(new MultiplayerError("The multiplayer server did not answer in time.", "TIMEOUT")); return; }
        if (!response?.ok) { reject(new MultiplayerError(response?.error || "Multiplayer request failed.", response?.code)); return; }
        resolve(response as T);
      });
    });
  }

  private remember(credentials: MultiplayerCredentials) {
    this.credentials = credentials;
    sessionStorage.setItem(sessionKey(credentials.roomCode), JSON.stringify(credentials));
    sessionStorage.setItem(LAST_SESSION, credentials.roomCode);
  }

  savedSession(code?: string | null): MultiplayerCredentials | null {
    const roomCode = (code || sessionStorage.getItem(LAST_SESSION) || "").toUpperCase();
    if (!roomCode) return null;
    try { return JSON.parse(sessionStorage.getItem(sessionKey(roomCode)) || "null") as MultiplayerCredentials | null; } catch { return null; }
  }

  async createRoom(name: string, maxPlayers = 4) {
    const result = await this.request<{ room: MultiplayerRoom; credentials: MultiplayerCredentials }>("room:create", { name, maxPlayers });
    this.room = result.room; this.remember(result.credentials); return result;
  }

  async joinRoom(code: string, name: string, spectator = false) {
    const result = await this.request<{ room: MultiplayerRoom; credentials: MultiplayerCredentials; game?: MultiplayerGameUpdate | null }>("room:join", { code: code.toUpperCase(), name, spectator });
    this.room = result.room; this.remember(result.credentials); return result;
  }

  private refreshCredentials(room: MultiplayerRoom) {
    if (!this.credentials) return;
    const own = room.players.find(player => player.id === this.credentials!.playerId);
    const spectator = room.spectators.find(entry => entry.id === this.credentials!.playerId);
    if (!own && !spectator) return;
    this.remember({ ...this.credentials, faction: own?.faction ?? null, isHost: room.hostId === this.credentials!.playerId, isSpectator: !!spectator });
  }

  async resume(credentials = this.credentials || this.savedSession()) {
    if (!credentials) throw new MultiplayerError("No saved multiplayer session was found.", "NO_SESSION");
    const result = await this.request<{ room: MultiplayerRoom; credentials: MultiplayerCredentials; game: MultiplayerGameUpdate | null }>("room:resume", { code: credentials.roomCode, playerId: credentials.playerId, token: credentials.token });
    this.room = result.room; this.revision = result.game?.revision || result.room.revision; this.remember(result.credentials); return result;
  }

  async updatePlayer(patch: { name?: string; faction?: string; team?: number }) {
    const result = await this.request<{ room: MultiplayerRoom }>("player:update", { patch }); this.room = result.room; this.refreshCredentials(result.room); return result.room;
  }

  async setAiSlot(position: number, enabled: boolean) {
    const result = await this.request<{ room: MultiplayerRoom }>("slot:ai", { position, enabled });
    this.room = result.room; this.refreshCredentials(result.room); return result.room;
  }
  async updatePlayerPosition(playerId: string, position: number) {
    const result = await this.request<{ room: MultiplayerRoom }>("player:position", { playerId, position });
    this.room = result.room; this.refreshCredentials(result.room); return result.room;
  }
  async updatePlayerTeam(playerId: string, team: number) {
    const result = await this.request<{ room: MultiplayerRoom }>("player:team", { playerId, team });
    this.room = result.room; this.refreshCredentials(result.room); return result.room;
  }
  async updatePlayerFaction(playerId: string, faction: string) {
    const result = await this.request<{ room: MultiplayerRoom }>("player:faction", { playerId, faction });
    this.room = result.room; this.refreshCredentials(result.room); return result.room;
  }
  async demotePlayer(playerId: string) {
    const result = await this.request<{ room: MultiplayerRoom }>("player:demote", { playerId }); this.room = result.room; this.refreshCredentials(result.room); return result.room;
  }
  async promoteSpectator(playerId: string) {
    const result = await this.request<{ room: MultiplayerRoom }>("spectator:promote", { playerId }); this.room = result.room; this.refreshCredentials(result.room); return result.room;
  }

  async configure(config: MultiplayerLobbyConfig) {
    const result = await this.request<{ room: MultiplayerRoom }>("lobby:configure", { config }); this.room = result.room; this.refreshCredentials(result.room); return result.room;
  }

  async startGame(state: GameState) {
    const result = await this.request<{ room: MultiplayerRoom; state: GameState; revision: number }>("game:start", { state });
    this.room = result.room; this.revision = result.revision; return result;
  }

  async undoGame() {
    const result = await this.request<MultiplayerGameUpdate>("game:undo", { baseRevision: this.revision }); this.revision = result.revision; return result;
  }

  async commitGame(state: GameState) {
    const result = await this.request<MultiplayerGameUpdate>("game:commit", { baseRevision: this.revision, state });
    this.revision = result.revision; return result;
  }

  async respondRetaliation(retaliate: boolean) {
    const result = await this.request<MultiplayerGameUpdate>("game:retaliation", { retaliate });
    this.revision = result.revision; return result;
  }

  async leave(terminate = false) {
    try { await this.request<{ room: MultiplayerRoom | null }>("room:leave", { terminate }); } finally {
      if (this.credentials) sessionStorage.removeItem(sessionKey(this.credentials.roomCode));
      sessionStorage.removeItem(LAST_SESSION); this.credentials = null; this.room = null; this.revision = 0;
    }
  }

  pause() { this.socket.disconnect(); }

  onRoom(handler: (room: MultiplayerRoom) => void) {
    const listener = (room: MultiplayerRoom) => { this.room = room; this.revision = room.revision; this.refreshCredentials(room); handler(room); };
    this.socket.on("room:state", listener); return () => { this.socket.off("room:state", listener); };
  }
  onGameStarted(handler: (update: MultiplayerGameUpdate & { room: MultiplayerRoom }) => void) {
    const listener = (update: MultiplayerGameUpdate & { room: MultiplayerRoom }) => { this.room = update.room; this.revision = update.revision; handler(update); };
    this.socket.on("game:started", listener); return () => { this.socket.off("game:started", listener); };
  }
  onGameState(handler: (update: MultiplayerGameUpdate) => void) {
    const listener = (update: MultiplayerGameUpdate) => { this.revision = update.revision; handler(update); };
    this.socket.on("game:state", listener); return () => { this.socket.off("game:state", listener); };
  }
  onRoomClosed(handler: () => void) {
    const listener = () => handler();
    this.socket.on("room:closed", listener); return () => { this.socket.off("room:closed", listener); };
  }
  onConnection(handler: (connected: boolean) => void) {
    const yes = () => handler(true), no = () => handler(false);
    this.socket.on("connect", yes); this.socket.on("disconnect", no); handler(this.socket.connected);
    return () => { this.socket.off("connect", yes); this.socket.off("disconnect", no); };
  }
}

let singleton: MultiplayerClient | null = null;
export const multiplayerClient = () => singleton ??= new MultiplayerClient();

export function invitedRoomCode() {
  const search = new URLSearchParams(location.search).get("room");
  const hash = new URLSearchParams(location.hash.split("?")[1] || "").get("room");
  return (search || hash || "").trim().toUpperCase();
}

export function inviteUrl(code: string) {
  const url = new URL(location.href); url.searchParams.set("room", code.toUpperCase()); url.hash = "/multiplayer"; return url.toString();
}

export function clearInviteFromUrl() {
  const url = new URL(location.href); url.searchParams.delete("room"); history.replaceState(null, "", `${url.pathname}${url.search}#/multiplayer`);
}
